/**
 * Audio Utilities for DJ Studio
 */

// MPEG-1/2 Layer III only defines these sample rates. Anything else cannot be encoded
// to MP3 without resampling first, so callers fall back to WAV.
const MP3_SAMPLE_RATES = [8000, 11025, 12000, 16000, 22050, 24000, 32000, 44100, 48000];

/**
 * Encodes an AudioBuffer to MP3 in a Web Worker.
 * @param {AudioBuffer} buffer
 * @param {{ kbps?: number, onProgress?: (pct: number) => void }} options
 * @returns {Promise<Blob>} audio/mpeg blob
 */
export function audioBufferToMp3(buffer, { kbps = 192, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    if (!MP3_SAMPLE_RATES.includes(buffer.sampleRate)) {
      reject(new Error(`Sample rate ${buffer.sampleRate}Hz is not valid for MP3`));
      return;
    }

    let worker;
    try {
      worker = new Worker(new URL("../workers/mp3Encoder.worker.js", import.meta.url), {
        type: "module"
      });
    } catch (e) {
      reject(e);
      return;
    }

    // Copy the channel data so the underlying buffers can be transferred to the worker
    // without detaching the AudioBuffer's own memory.
    const left = buffer.getChannelData(0).slice();
    const right = buffer.numberOfChannels > 1 ? buffer.getChannelData(1).slice() : null;

    worker.onmessage = (event) => {
      const data = event.data;
      if (data.type === "progress") {
        if (onProgress) onProgress(data.progress);
        return;
      }
      if (data.type === "done") {
        worker.terminate();
        resolve(new Blob([data.buffer], { type: "audio/mpeg" }));
        return;
      }
      if (data.type === "error") {
        worker.terminate();
        reject(new Error(data.message));
      }
    };

    worker.onerror = (e) => {
      worker.terminate();
      reject(e.error || new Error("MP3 encoder worker failed"));
    };

    const transfer = right ? [left.buffer, right.buffer] : [left.buffer];
    worker.postMessage({ left, right, sampleRate: buffer.sampleRate, kbps }, transfer);
  });
}

/**
 * Encodes an AudioBuffer into 16-bit Stereo WAV file format
 * @param {AudioBuffer} buffer
 * @returns {Blob}
 */
export function audioBufferToWav(buffer) {
  const numOfChan = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // 1 = raw LPCM
  const bitDepth = 16;
  
  let result;
  if (numOfChan === 2) {
    result = interleave(buffer.getChannelData(0), buffer.getChannelData(1));
  } else {
    // If mono, convert to stereo by duplicating channel
    const monoData = buffer.getChannelData(0);
    result = interleave(monoData, monoData);
  }
  
  const bufferLength = result.length * 2; // 16-bit = 2 bytes per sample
  const wavBuffer = new ArrayBuffer(44 + bufferLength);
  const view = new DataView(wavBuffer);
  
  // RIFF header
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + bufferLength, true);
  writeString(view, 8, "WAVE");
  
  // fmt chunk
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true); // chunk length
  view.setUint16(20, format, true); // raw PCM
  view.setUint16(22, 2, true); // force 2 channels (stereo)
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2 * (bitDepth / 8), true); // byte rate
  view.setUint16(32, 2 * (bitDepth / 8), true); // block align
  view.setUint16(34, bitDepth, true); // bits per sample
  
  // data chunk
  writeString(view, 36, "data");
  view.setUint32(40, bufferLength, true);
  
  // Write audio samples
  floatTo16BitPCM(view, 44, result);
  
  return new Blob([view], { type: "audio/wav" });
}

function interleave(inputL, inputR) {
  const length = inputL.length + inputR.length;
  const result = new Float32Array(length);
  let index = 0;
  let inputIndex = 0;
  
  while (index < length) {
    result[index++] = inputL[inputIndex];
    result[index++] = inputR[inputIndex];
    inputIndex++;
  }
  return result;
}

function floatTo16BitPCM(output, offset, input) {
  for (let i = 0; i < input.length; i++, offset += 2) {
    let s = Math.max(-1, Math.min(1, input[i]));
    output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

/**
 * Creates a synthesized convolution reverb impulse response buffer
 * @param {AudioContext} ctx 
 * @param {number} duration seconds
 * @returns {AudioBuffer}
 */
export function createReverbImpulse(ctx, duration = 2.0) {
  const rate = ctx.sampleRate;
  const len = rate * duration;
  const impulse = ctx.createBuffer(2, len, rate);
  const left = impulse.getChannelData(0);
  const right = impulse.getChannelData(1);

  for (let i = 0; i < len; i++) {
    const pct = i / len;
    // Exponential decay envelope
    const envelope = Math.exp(-pct * 4) * (1 - pct);
    // Random white noise
    left[i] = (Math.random() * 2 - 1) * envelope;
    right[i] = (Math.random() * 2 - 1) * envelope;
  }

  return impulse;
}

/**
 * Estimates BPM from an AudioBuffer
 * (Basic Peak Energy Envelope Beat Detection)
 * @param {AudioBuffer} buffer 
 * @returns {Promise<number>}
 */
export async function detectBPM(buffer) {
  // If buffer is too long, analyze only the middle 60 seconds to speed up
  const sampleRate = buffer.sampleRate;
  const maxSec = 60;
  const startOffset = Math.floor(Math.max(0, (buffer.duration / 2) - (maxSec / 2)) * sampleRate);
  const endOffset = Math.min(buffer.length, startOffset + maxSec * sampleRate);
  
  // Extract mono channel data
  const data = buffer.getChannelData(0).subarray(startOffset, endOffset);
  
  // Downsample to ~4000Hz to make analysis fast
  const fitRate = 4000;
  const sampleStep = Math.round(sampleRate / fitRate);
  const downsampled = [];
  
  for (let i = 0; i < data.length; i += sampleStep) {
    downsampled.push(data[i]);
  }
  
  // Apply a simple absolute value / low pass filter on the downsampled signal envelope
  const envelope = new Float32Array(downsampled.length);
  let lastVal = 0;
  const filterCoef = 0.98; // Low pass filter coefficient
  
  for (let i = 0; i < downsampled.length; i++) {
    const absVal = Math.abs(downsampled[i]);
    envelope[i] = filterCoef * lastVal + (1 - filterCoef) * absVal;
    lastVal = envelope[i];
  }
  
  // Locate local peaks in the envelope
  // Compute local threshold (running average of energy)
  const windowSize = Math.round(fitRate * 0.15); // 150ms window
  let windowSum = 0;
  for (let i = 0; i < windowSize; i++) {
    windowSum += envelope[i] || 0;
  }
  
  const peaks = [];
  const minPeakDistance = Math.round(fitRate * 0.3); // min 300ms between beats (max ~200 bpm)
  let lastPeakIndex = -minPeakDistance;
  
  for (let i = windowSize; i < envelope.length - windowSize; i++) {
    // Sliding window sum update
    windowSum = windowSum - (envelope[i - windowSize] || 0) + (envelope[i + windowSize] || 0);
    const avg = windowSum / (windowSize * 2);
    const threshold = avg * 1.5; // Peak threshold is 1.5x of local running average
    
    const val = envelope[i];
    if (val > threshold && val > envelope[i - 1] && val > envelope[i + 1]) {
      if (i - lastPeakIndex > minPeakDistance) {
        peaks.push(i);
        lastPeakIndex = i;
      }
    }
  }
  
  if (peaks.length < 5) {
    return 128; // Fallback
  }
  
  // Analyze peak intervals to find intervals that align to a tempo
  const intervals = [];
  for (let i = 1; i < peaks.length; i++) {
    intervals.push(peaks[i] - peaks[i - 1]);
  }
  
  // Group intervals into bins to find the most common interval
  const intervalCounts = {};
  const binResolution = Math.round(fitRate * 0.02); // 20ms bin resolution
  
  intervals.forEach(interval => {
    const bin = Math.round(interval / binResolution) * binResolution;
    intervalCounts[bin] = (intervalCounts[bin] || 0) + 1;
  });
  
  let maxCount = 0;
  let dominantInterval = 0;
  Object.keys(intervalCounts).forEach(bin => {
    if (intervalCounts[bin] > maxCount) {
      maxCount = intervalCounts[bin];
      dominantInterval = parseFloat(bin);
    }
  });
  
  if (dominantInterval === 0) return 128;
  
  // Convert interval in samples back to BPM
  const intervalInSec = dominantInterval / fitRate;
  let bpm = 60 / intervalInSec;
  
  // Keep BPM in standard DJ range: 60 - 180
  while (bpm < 60) bpm *= 2;
  while (bpm > 180) bpm /= 2;
  
  return Math.round(bpm);
}
